import type { AnalyticsProvider, AnalyticsEventName, AnalyticsPayload } from './types';
import { hasTrackingConsent, isTrackingDeclined } from '@/features/consent/consent';
import { getAttributionData, getOrCreateAnonymousId, getOrCreateSessionId } from './attribution';
import { consoleProvider } from './providers/console';
import { gaProvider } from './providers/ga';
import { pixelProvider } from './providers/pixel';

const providers: AnalyticsProvider[] = [];

type EventParams = Partial<Omit<AnalyticsPayload, 'eventName' | 'attribution'>>;

type QueuedCall =
  | { type: 'event'; eventName: AnalyticsEventName; params: EventParams }
  | { type: 'page'; path: string };

const queue: QueuedCall[] = [];
let booted = false;

function dispatchEvent(eventName: AnalyticsEventName, params: EventParams) {
  const payload: AnalyticsPayload = {
    eventName,
    anonymousId: getOrCreateAnonymousId(),
    sessionId: getOrCreateSessionId(),
    attribution: getAttributionData(),
    ...params,
  };

  providers.forEach((p) => {
    try { p.track(eventName, payload); } catch { /* swallow */ }
  });
}

function dispatchPage(path: string) {
  providers.forEach((p) => {
    try { p.page(path); } catch { /* swallow */ }
  });
}

function flushAnalyticsQueue() {
  const pending = queue.splice(0);
  pending.forEach((item) => {
    if (item.type === 'page') dispatchPage(item.path);
    else dispatchEvent(item.eventName, item.params);
  });
}

export function initAnalytics() {
  if (typeof window === 'undefined' || !hasTrackingConsent()) return;

  if (!booted) {
    booted = true;
    providers.push(gaProvider, pixelProvider);

    if (import.meta.env.DEV) {
      providers.push(consoleProvider);
    }

    providers.forEach((p) => {
      try { p.init(); } catch { /* never block app */ }
    });
  }

  flushAnalyticsQueue();
}

/** Drop events captured before a Decline all choice. Scripts stay unloaded. */
export function discardAnalyticsQueue() {
  queue.length = 0;
}

export function registerProvider(provider: AnalyticsProvider) {
  providers.push(provider);
  if (!booted || !hasTrackingConsent()) return;
  try { provider.init(); } catch { /* ignore */ }
}

export function trackEvent(eventName: AnalyticsEventName, params: EventParams = {}) {
  if (typeof window === 'undefined' || isTrackingDeclined()) return;

  if (!booted) {
    queue.push({ type: 'event', eventName, params });
    return;
  }

  dispatchEvent(eventName, params);
}

export function trackPageView(path: string) {
  if (typeof window === 'undefined' || isTrackingDeclined()) return;

  if (!booted) {
    queue.push({ type: 'page', path });
    return;
  }

  dispatchPage(path);
}
